import { useMemo } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { createAgentsConfig } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';

import { areStringArraysEqual } from './areStringArraysEqual';
import { createUIConfig } from './common/utils/ui-config/createUIConfig';
import { type ConversationAssistantProps } from './types';
import { useSemanticStableValue } from './useSemanticStableValue';

type ProviderValueInputs = {
	agentsConfig?: ConversationAssistantProps['agentsConfig'];
	uiConfig?: ConversationAssistantProps['uiConfig'];
};
type MemoizedProviderValue<Value> = { value: Value } | undefined;

const areAgentsConfigsEqual = (
	current?: ConversationAssistantProps['agentsConfig'],
	next?: ConversationAssistantProps['agentsConfig'],
): boolean =>
	current?.defaultAgentId === next?.defaultAgentId &&
	areStringArraysEqual(current?.filter, next?.filter);

const getUIConfigForShallowCompare = (
	config?: ConversationAssistantProps['uiConfig'],
): Record<string, unknown> => {
	const { customHandlers: _customHandlers, elements: _elements, ...shallowConfig } = config ?? {};
	return shallowConfig;
};

const hasCustomHandlersProperty = (config?: ConversationAssistantProps['uiConfig']): boolean =>
	Object.prototype.hasOwnProperty.call(config ?? {}, 'customHandlers');

const areUIConfigsEqual = (
	current?: ConversationAssistantProps['uiConfig'],
	next?: ConversationAssistantProps['uiConfig'],
): boolean =>
	shallowEqualObjects(getUIConfigForShallowCompare(current), getUIConfigForShallowCompare(next)) &&
	shallowEqualObjects(current?.elements ?? {}, next?.elements ?? {}) &&
	hasCustomHandlersProperty(current) === hasCustomHandlersProperty(next) &&
	shallowEqualObjects(current?.customHandlers, next?.customHandlers);

export const useConversationAssistantProviderValues = ({
	agentsConfig,
	uiConfig,
}: ProviderValueInputs): {
	agentOverriderValue: ReturnType<typeof createAgentsConfig>;
	uiConfigValue: ReturnType<typeof createUIConfig>;
} => {
	const isTreatmentEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;

	const stableAgentsConfig = useSemanticStableValue(
		agentsConfig,
		isTreatmentEnabled,
		areAgentsConfigsEqual,
	);
	const stableUIConfig = useSemanticStableValue(uiConfig, isTreatmentEnabled, areUIConfigsEqual);
	const memoizedAgentOverriderValue: MemoizedProviderValue<ReturnType<typeof createAgentsConfig>> =
		useMemo(
			() =>
				isTreatmentEnabled
					? {
							value: createAgentsConfig(stableAgentsConfig),
						}
					: undefined,
			[isTreatmentEnabled, stableAgentsConfig],
		);
	const memoizedUIConfigValue: MemoizedProviderValue<ReturnType<typeof createUIConfig>> = useMemo(
		() =>
			isTreatmentEnabled
				? {
						value: createUIConfig(stableUIConfig),
					}
				: undefined,
		[isTreatmentEnabled, stableUIConfig],
	);

	if (memoizedAgentOverriderValue && memoizedUIConfigValue) {
		return {
			agentOverriderValue: memoizedAgentOverriderValue.value,
			uiConfigValue: memoizedUIConfigValue.value,
		};
	}

	return {
		agentOverriderValue: createAgentsConfig(agentsConfig),
		uiConfigValue: createUIConfig(uiConfig),
	};
};
